
import { Button } from "@/components/ui/button";
import { CheckCircle2, ShoppingCart, Sparkles, Wand2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import React from "react";
import { useTranslation } from 'react-i18next';
import HighlightedText from './HighlightedText';
import HeroInteractiveDemo from './HeroInteractiveDemo';


const Hero: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const scrollToDemo = () => {
    const demoSection = document.getElementById("demo");
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="hero" className="relative overflow-hidden bg-linear-to-br from-sky-50 via-emerald-50 to-yellow-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden="true" />
      <div className="container mx-auto px-4 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Copy */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-sm text-foreground shadow-sm backdrop-blur">
              <Sparkles className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              {t('hero.badge')}
            </div>

            <h1 className="mt-3 sm:mt-4 text-3xl md:text-6xl font-extrabold tracking-tight text-foreground">{t('hero.title')}</h1>
            <p className="mt-3 sm:mt-4 text-base md:text-xl text-muted-foreground leading-relaxed sm:block">
              <HighlightedText text={t('hero.subtitle')} />
            </p>

            <ul className="mt-6 space-y-3 text-foreground">
              <li className="hidden xs:flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />{t('hero.benefit1')}</li>
              <li className="hidden xs:flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />{t('hero.benefit2')}</li>
              <li className="hidden xs:flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />{t('hero.benefit3')}</li>
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button onClick={scrollToDemo} className="bg-linear-to-r from-emerald-500 to-sky-500 hover:from-emerald-600 hover:to-sky-600 text-white px-8 py-6 text-lg rounded-full shadow-lg transition-all duration-300">
                <Wand2 className="mr-2 h-5 w-5" /> {t('hero.ctaDemo')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/login")}
                className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 px-8 py-6 text-lg rounded-full"
              >
                <ShoppingCart className="mr-2 h-5 w-5" /> {t('hero.ctaStart')}
              </Button>
            </div>

            <div className="mt-4 sm:mt-6 text-sm text-muted-foreground hidden sm:block">{t('hero.lovedBy')}</div>
          </div>

          {/* Interactive demo with slideshow */}
          <HeroInteractiveDemo />
        </div>
      </div>
    </section>
  );
};

export default Hero;
